import {
  BarChart,
  DataTable,
  EmptyState,
  LineChart,
  List,
  ListRow,
  Section,
  Select,
  Skeleton,
  StatCard,
  Text,
} from '@ui';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { errorMessage } from '../../../lib/errors';
import { useUrlState } from '../../../lib/useUrlState';
import { formatKg } from '../../harvest/format';
import { useYieldReport, type YieldReport } from '../api';
import { change, yearOptions } from '../format';
import styles from './ReportPages.module.css';

/** Yield per year, variety, colony and location (RPT-010 – RPT-014). */
export function YieldPage() {
  const { t } = useTranslation();
  const thisYear = new Date().getFullYear();
  const [yearParam, setYear] = useUrlState('jahr', String(thisYear));
  const year = Number(yearParam) || thisYear;
  const report = useYieldReport(year);
  return (
    <div className={styles.page}>
      <div className={styles.year}>
        <Select
          size="sm"
          aria-label={t('reports.year')}
          value={String(year)}
          onChange={(e) => setYear(e.target.value)}
          options={yearOptions(thisYear, [year, ...(report.data?.years.map((y) => y.year) ?? [])])}
        />
      </div>
      {report.error ? (
        <Text tone="danger" role="alert">
          {errorMessage(report.error)}
        </Text>
      ) : !report.data ? (
        <div role="status" aria-label={t('common.loading')}>
          <Skeleton height="20rem" shape="rect" />
        </div>
      ) : (
        <Yield report={report.data} />
      )}
    </div>
  );
}

function Yield({ report }: { report: YieldReport }) {
  const { t } = useTranslation();
  const year = report.year;
  const total = report.years.find((y) => y.year === year);
  const previous = report.years.find((y) => y.year === year - 1);
  const grams = total?.grams ?? 0;
  const kg = (g: number) => formatKg(g, 1);
  const recentYears = report.years.map((y) => y.year).slice(-4);
  const yearColumns = <T extends { years: { year: number; grams: number }[] }>() =>
    recentYears.map((y) => ({
      key: `y${y}`,
      header: String(y),
      align: 'end' as const,
      cell: (row: T) => {
        const g = row.years.find((v) => v.year === y)?.grams ?? 0;
        return g ? kg(g) : '–';
      },
    }));
  return (
    <>
      <section aria-label={t('reports.figures')} className={styles.figures}>
        <StatCard
          label={t('reports.yield.total', { year })}
          value={kg(grams)}
          delta={change(grams, previous?.grams ?? 0, t('reports.vsPrevious', { year: year - 1 }))}
        />
        <StatCard
          label={t('reports.yield.average')}
          value={total?.average_grams != null ? kg(total.average_grams) : '–'}
          footnote={t('reports.yield.activeColonies', { count: total?.active_colonies ?? 0 })}
        />
        <StatCard
          label={t('reports.yield.previous', { year: year - 1 })}
          value={kg(previous?.grams ?? 0)}
        />
      </section>
      {grams === 0 && report.colonies.length === 0 ? (
        <EmptyState
          title={t('reports.yield.emptyTitle', { year })}
          actions={<Link to="/ernte">{t('reports.yield.toHarvest')}</Link>}
        />
      ) : (
        <>
          <div className={styles.columns}>
            {report.years.length > 1 && (
              <Section title={t('reports.yield.years')} variant="content">
                <LineChart
                  label={t('reports.yield.years')}
                  categoryHeader={t('reports.yearColumn')}
                  categories={report.years.map((y) => String(y.year))}
                  format={(v) => kg(v)}
                  series={[
                    {
                      key: 'total',
                      label: t('reports.yield.totalShort'),
                      values: report.years.map((y) => y.grams),
                    },
                    {
                      key: 'average',
                      label: t('reports.yield.averageShort'),
                      values: report.years.map((y) => y.average_grams ?? 0),
                    },
                  ]}
                />
              </Section>
            )}
            {report.varieties.length > 0 && report.varieties.length <= 6 && (
              <Section title={t('reports.yield.varieties')} variant="content">
                <BarChart
                  label={t('reports.yield.varieties')}
                  categoryHeader={t('reports.yield.variety')}
                  categories={report.varieties.map((v) => v.variety.name)}
                  format={(v) => kg(v)}
                  series={[
                    {
                      key: 'year',
                      label: String(year),
                      values: report.varieties.map((v) => v.grams),
                    },
                    {
                      key: 'previous',
                      label: String(year - 1),
                      values: report.varieties.map((v) => v.previous_grams),
                    },
                  ]}
                />
              </Section>
            )}
            {report.varieties.length > 6 && (
              <Section title={t('reports.yield.varieties')}>
                <List>
                  {report.varieties.map((v) => (
                    <ListRow
                      key={v.variety.id}
                      title={v.variety.name}
                      description={t('reports.previousAmount', {
                        year: year - 1,
                        amount: kg(v.previous_grams),
                      })}
                      trailing={kg(v.grams)}
                    />
                  ))}
                </List>
              </Section>
            )}
          </div>
          <Section variant="flush" title={t('reports.yield.colonies')}>
            <DataTable
              caption={t('reports.yield.colonies')}
              rows={report.colonies}
              rowKey={(c) => c.item.id}
              rowHref={(c) => `/voelker/${c.item.id}`}
              empty={
                <Text tone="muted" className={styles.empty}>
                  {t('reports.yield.noShares')}
                </Text>
              }
              columns={[
                {
                  key: 'rank',
                  header: '#',
                  width: '3rem',
                  mobile: 'hidden',
                  cell: (c) => (c.grams > 0 ? report.colonies.indexOf(c) + 1 : ''),
                },
                {
                  key: 'name',
                  header: t('reports.yield.colony'),
                  primary: true,
                  cell: (c) => c.item.name,
                },
                ...yearColumns<YieldReport['colonies'][number]>(),
              ]}
            />
          </Section>
          {report.locations.length > 0 && (
            <Section variant="flush" title={t('reports.yield.locations')}>
              <DataTable
                caption={t('reports.yield.locations')}
                rows={report.locations}
                rowKey={(l) => l.item.id}
                rowHref={(l) => `/standorte/${l.item.id}`}
                columns={[
                  {
                    key: 'name',
                    header: t('reports.yield.location'),
                    primary: true,
                    cell: (l) => l.item.name,
                  },
                  ...yearColumns<YieldReport['locations'][number]>(),
                ]}
              />
            </Section>
          )}
        </>
      )}
    </>
  );
}
